import {
  Directive,
  inject,
  input,
  InputSignal,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { DefinedPermission } from '@/common/models/permission.model';
import { PermissionService } from '@/common/services';
import { LayoutService } from '@/layout/services/layout.service';

// 1. Definimos la nueva estructura para el input
export interface PermissionCheckConfig {
  permissions: DefinedPermission[];
  action: (event?: MouseEvent) => void; // La función a ejecutar
}

@Directive({
  selector: '[appPermissionCheck]',
  standalone: true,
})
export class PermissionCheckDirective implements OnInit {
  private readonly _templateRef = inject(TemplateRef<unknown>);
  private readonly _viewContainer = inject(ViewContainerRef);
  private readonly _permissionService = inject(PermissionService);
  private readonly _layoutService = inject(LayoutService);
  private readonly _renderer = inject(Renderer2);
  private readonly _messageService = inject(MessageService);
  private readonly _translate = inject(TranslateService);

  // Lista de permisos que activarán la lógica de 'deshabilitar y mostrar diálogo'.
  private readonly definedPermissions: DefinedPermission[] = [
    'Offer:edit',
    'Offer:pause',
    'Offer:close',
    'Offer:create',
    'Offer:delete',
    'Offer:view',
    'User:create',
    'User:edit',
    'User:delete',
    'User:can_edit_admin',
    'User:can_delete_admin',
    'User:view',
    'ApplyOffer:edit',
    'ApplyOffer:delete',
    'ApplyOffer:view',
    'Entity:edit',
    'Entity:can_edit_data_management',
    'EntityLocation:view',
    'EntityLocation:create',
    'EntityLocation:edit',
    'EntityLocation:delete',
  ];

  readonly config: InputSignal<PermissionCheckConfig> = input.required({ alias: 'appPermissionCheck' });

  ngOnInit(): void {
    // Solo leemos los permisos iniciales para verificar el acceso.
    // La `action` se lee en el momento del click (this.config().action) para
    // garantizar que siempre use la versión más reciente con los IDs correctos.
    const { permissions } = this.config();

    this._permissionService.checkPermissions(permissions)
      .pipe(take(1))
      .subscribe((hasPermissions: boolean) => {
        this._viewContainer.clear();

        // Determina si el elemento debe mostrarse (aunque sea deshabilitado)
        const shouldRenderElement = hasPermissions || permissions.some(p => this.definedPermissions.includes(p));

        if (shouldRenderElement) {
          const viewRef = this._viewContainer.createEmbeddedView(this._templateRef);
          const element = viewRef.rootNodes.length > 0 ? viewRef.rootNodes[0] : null;

          if (element) {
            if (hasPermissions) {
              // CASO 1: Tiene permisos. Adjuntamos la acción real.
              // Se lee this.config().action en el momento del click para obtener
              // siempre la acción actualizada (con los IDs de candidatos correctos).
              this._renderer.listen(element, 'click', (event: MouseEvent) => {
                event.stopPropagation();
                this.config().action(event);
              });
            } else {
              // CASO 2: No tiene permisos, pero se debe mostrar. Adjuntamos la acción del diálogo.
              this._renderer.addClass(element, 'permission-denied-pointer');
              this._renderer.listen(element, 'click', (event: MouseEvent) => {
                event.stopPropagation();
                this._messageService.add({
                  severity: 'error',
                  summary: this._translate.instant('COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.TITLE'),
                  detail: this._translate.instant('COMMON.COMPONENTS.ACCESS_DENIED_DIALOG.MESSAGE'),
                  life: 4500,
                });
              });
            }
          }
        }
        // CASO 3: No tiene permisos y no debe mostrarse. No se renderiza nada.
      });
  }
}
